import type { ColumnDef } from '@tanstack/react-table'
import { Download, MoreHorizontal } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { CompactTable, Hint, SectionTitle, Stat, Stats } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/components/ui/select'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from 'shared/components/ui/sheet'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate, type TranslationKey } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { formatDateTime, formatDayTime } from 'shared/lib/utils/date'
import { firstOfMonth, readableDay, tashkentDay } from './register-days'
import { saveRegisterFile } from './register-file'

/**
 * The Registers a partner is paid by (ADR-0036, spec §17, §19; ticket 16).
 *
 *  - **The next Register:** for a partner and a period — where its last Register
 *    ended (or, for its first, the day the cashier names) up to the last day chosen —
 *    every payment whose cash the central cashbox received in it, and the
 *    cancellations as minus lines; support's cancellations whose cash is not at the
 *    cashbox yet wait below. The cashier builds it once the period has ended.
 *  - **The Registers:** a table, each "to pay" until the cashier marks it paid with
 *    the payment order number (a dialog); a paid Register never changes. A row opens
 *    the Register in a side panel — its lines — and downloads as the Excel file the
 *    partner gets.
 *
 * In the hub's look (the redesign's reference): the partner and the period in one row,
 * the totals as one line of figures, the lines as tables, explanations behind an «i».
 *
 * Shared by the central cashbox (`canWork` — the cashier and the admin build and
 * mark) and the support section (reads; its cancellations after the Shift, from a
 * Parcel's card, are the minus lines). What the server answers is rendered as it
 * came: the lines, the totals, a refusal by its reason. The calls are injected so
 * the screen is tested without a backend; they default to the real tRPC client.
 */

export type RegisterPartner = RouterOutputs['partners']['Names'][number]
export type RegisterPreviewAnswer = RouterOutputs['registers']['Preview']
export type RegisterBuildAnswer = RouterOutputs['registers']['Build']
export type RegisterMarkPaidAnswer = RouterOutputs['registers']['MarkPaid']
export type RegisterSummary = RouterOutputs['registers']['List'][number]
export type RegisterDetail = RouterOutputs['registers']['Detail']
export type RegisterFile = RouterOutputs['registers']['Export']
type PeriodInput = RouterInputs['registers']['Preview']
type Line = RegisterDetail['lines'][number]
type Preview = Extract<RegisterPreviewAnswer, { outcome: 'preview' }>['preview']
type Totals = RegisterDetail['totals']

export interface RegisterCalls {
	listPartners: () => Promise<RegisterPartner[]>
	preview: (input: PeriodInput) => Promise<RegisterPreviewAnswer>
	build: (input: PeriodInput & { toDay: string }) => Promise<RegisterBuildAnswer>
	list: () => Promise<RegisterSummary[]>
	detail: (registerId: string) => Promise<RegisterDetail>
	markPaid: (input: { registerId: string; paymentOrder: string }) => Promise<RegisterMarkPaidAnswer>
	exportFile: (registerId: string) => Promise<RegisterFile>
	saveFile: (file: RegisterFile) => void
}

const viaTrpc: RegisterCalls = {
	listPartners: () => trpcClient.partners.Names.query(),
	preview: (input) => trpcClient.registers.Preview.query(input),
	build: (input) => trpcClient.registers.Build.mutate(input),
	list: () => trpcClient.registers.List.query({}),
	detail: (registerId) => trpcClient.registers.Detail.query({ registerId }),
	markPaid: (input) => trpcClient.registers.MarkPaid.mutate(input),
	exportFile: (registerId) => trpcClient.registers.Export.query({ registerId }),
	saveFile: saveRegisterFile,
}

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

const NUMBER = { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' }
/** Lines on one page of a table; the file holds them all. */
const PAGE_SIZE = 50

function formatMoney(amount: number, currency = 'UZS'): string {
	return `${amount.toLocaleString('ru-RU')} ${currency}`
}

/** A sum without its currency — the currency is in the column's header: `25 000`, a minus line `−12 000`. */
function amount(n: number): string {
	return n < 0 ? `−${Math.abs(n).toLocaleString('ru-RU')}` : n.toLocaleString('ru-RU')
}

/** The currency as a header says it: «сум» for UZS. */
function useCurrency() {
	const { t } = useTranslate()
	return (currency: string) => (currency === 'UZS' ? t('partnerRegister.som') : currency)
}

export interface PartnerRegistersProps {
	/** The cashier and the admin build and mark; support only reads. */
	canWork?: boolean
	calls?: Partial<RegisterCalls>
	now?: () => Date
}

export function PartnerRegisters({ canWork = false, calls, now = () => new Date() }: PartnerRegistersProps) {
	const c = { ...viaTrpc, ...calls }
	const { t } = useTranslate()
	const [registers, setRegisters] = useState<RegisterSummary[] | null>(null)
	const [listError, setListError] = useState<string | null>(null)
	const [actionError, setActionError] = useState<string | null>(null)
	const [openId, setOpenId] = useState<string | null>(null)
	const [paying, setPaying] = useState<Pick<RegisterSummary, 'registerId' | 'number'> | null>(null)
	const list = c.list

	const reload = useCallback(() => {
		list().then(
			(next) => {
				setRegisters(next)
				setListError(null)
			},
			(e) => setListError(failure(e)),
		)
	}, [list])
	useEffect(() => reload(), [reload])

	const download = async (registerId: string) => {
		setActionError(null)
		try {
			c.saveFile(await c.exportFile(registerId))
		} catch (e) {
			setActionError(failure(e))
		}
	}

	return (
		<div className='flex flex-col gap-8' data-testid='partner-registers'>
			<NextRegister canWork={canWork} calls={c} now={now} onBuilt={reload} />
			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('partnerRegister.listTitle')} count={registers?.length} />
				{listError && (
					<p role='alert' className='text-sm text-destructive' data-testid='registers-list-error'>
						{listError}
					</p>
				)}
				{actionError && (
					<p role='alert' className='text-sm text-destructive' data-testid='registers-action-error'>
						{actionError}
					</p>
				)}
				{registers && (
					<RegistersList
						registers={registers}
						canWork={canWork}
						onOpen={setOpenId}
						onPay={setPaying}
						onDownload={download}
					/>
				)}
			</section>
			<Sheet open={openId !== null} onOpenChange={(open) => !open && setOpenId(null)}>
				{openId && (
					<OpenRegister key={openId} registerId={openId} canWork={canWork} calls={c} onPaid={reload} />
				)}
			</Sheet>
			<Dialog open={paying !== null} onOpenChange={(open) => !open && setPaying(null)}>
				{paying && (
					<MarkPaid
						register={paying}
						calls={c}
						onPaid={() => {
							setPaying(null)
							reload()
						}}
					/>
				)}
			</Dialog>
		</div>
	)
}

function StatusBadge({ register }: { register: Pick<RegisterSummary, 'status' | 'paid'> }) {
	const { t } = useTranslate()
	return register.status === 'paid' ? (
		<Badge variant='secondary' data-testid='register-status'>
			{t('partnerRegister.statusPaid', { order: register.paid?.paymentOrder ?? '' })}
		</Badge>
	) : (
		<Badge variant='outline' data-testid='register-status'>
			{t('partnerRegister.statusToPay')}
		</Badge>
	)
}

// ---------------------------------------------------------------------------
// The Registers
// ---------------------------------------------------------------------------

function RegistersList({
	registers,
	canWork,
	onOpen,
	onPay,
	onDownload,
}: {
	registers: RegisterSummary[]
	canWork: boolean
	onOpen: (registerId: string) => void
	onPay: (register: RegisterSummary) => void
	onDownload: (registerId: string) => void
}) {
	const { t } = useTranslate()
	const currency = useCurrency()
	const columns: ColumnDef<RegisterSummary>[] = [
		{
			id: 'number',
			header: t('partnerRegister.colNumber'),
			cell: ({ row }) => (
				<button
					type='button'
					className='font-mono font-semibold hover:underline'
					data-testid={`register-open-${row.original.registerId}`}
					onClick={(e) => {
						e.stopPropagation()
						onOpen(row.original.registerId)
					}}
				>
					{row.original.number}
				</button>
			),
		},
		{ id: 'partner', header: t('partnerRegister.colPartner'), cell: ({ row }) => row.original.partner.name },
		{
			id: 'period',
			header: t('partnerRegister.colPeriod'),
			cell: ({ row }) => (
				<span className='tabular-nums'>
					{readableDay(row.original.fromDay)} — {readableDay(row.original.toDay)}
				</span>
			),
		},
		{ id: 'lines', header: t('partnerRegister.colLines'), meta: NUMBER, cell: ({ row }) => row.original.totals.lines },
		{
			id: 'total',
			header: t('partnerRegister.colTotal', { currency: currency(registers[0]?.totals.currency ?? 'UZS') }),
			meta: NUMBER,
			cell: ({ row }) => amount(row.original.totals.total),
		},
		{ id: 'status', header: t('partnerRegister.colStatus'), cell: ({ row }) => <StatusBadge register={row.original} /> },
		{
			id: 'actions',
			header: '',
			meta: { cellClassName: 'w-px text-right' },
			cell: ({ row: { original: r } }) => (
				<div className='flex items-center justify-end gap-2' onClick={(e) => e.stopPropagation()}>
					{canWork && r.status === 'to_pay' && (
						<Button size='sm' variant='outline' data-testid={`register-pay-${r.registerId}`} onClick={() => onPay(r)}>
							{t('partnerRegister.markPaid')}
						</Button>
					)}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								size='icon-sm'
								variant='ghost'
								aria-label={t('partnerRegister.more')}
								data-testid={`register-more-${r.registerId}`}
							>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem onSelect={() => onOpen(r.registerId)}>{t('partnerRegister.open')}</DropdownMenuItem>
							<DropdownMenuItem
								data-testid={`register-download-${r.registerId}`}
								onSelect={() => onDownload(r.registerId)}
							>
								{t('partnerRegister.download')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			),
		},
	]
	const { table } = useDataTable({
		data: registers,
		columns,
		rowCount: registers.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (r) => r.registerId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	return (
		<div data-testid='registers-list'>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='registers-empty'>{t('partnerRegister.listEmpty')}</span>}
				row={{
					attributes: (r) => ({ 'data-testid': `register-row-${r.registerId}`, 'data-status': r.status }),
					onRowClick: (r) => onOpen(r.registerId),
				}}
			/>
		</div>
	)
}

// ---------------------------------------------------------------------------
// The next Register
// ---------------------------------------------------------------------------

function NextRegister({
	canWork,
	calls,
	now,
	onBuilt,
}: {
	canWork: boolean
	calls: RegisterCalls
	now: () => Date
	onBuilt: () => void
}) {
	const { t } = useTranslate()
	const [partners, setPartners] = useState<RegisterPartner[] | null>(null)
	const [partnerId, setPartnerId] = useState('')
	// The cashier names a period that has ended — yesterday by default; support reads up to now.
	const [toDay, setToDay] = useState(() => (canWork ? tashkentDay(now(), 1) : ''))
	const [today] = useState(() => tashkentDay(now()))
	const [fromDay, setFromDay] = useState('')
	const [needsFrom, setNeedsFrom] = useState(false)
	const [answer, setAnswer] = useState<RegisterPreviewAnswer | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [building, setBuilding] = useState(false)
	const [built, setBuilt] = useState<RegisterBuildAnswer | null>(null)
	// Read again after a build, even when the period's inputs stay the same.
	const [rev, setRev] = useState(0)
	const reads = useRef(0)
	const { listPartners, preview } = calls

	useEffect(() => {
		listPartners().then(
			(next) => {
				setPartners(next)
				if (next.length === 1) setPartnerId(next[0]!._id)
			},
			(e) => setError(failure(e)),
		)
	}, [listPartners])

	// A new partner: its first Register's day is asked only if the server says so.
	useEffect(() => {
		setNeedsFrom(false)
		setFromDay('')
		setBuilt(null)
	}, [partnerId])

	const period = useCallback(
		(): PeriodInput => ({
			partnerId,
			...(toDay ? { toDay } : {}),
			...(needsFrom && fromDay ? { fromDay } : {}),
		}),
		[partnerId, toDay, needsFrom, fromDay],
	)

	// What the next Register would hold is the server's: read afresh whenever the period changes.
	useEffect(() => {
		if (!partnerId) return
		if (needsFrom && !fromDay) return
		const read = ++reads.current
		setAnswer(null)
		setError(null)
		preview(period()).then(
			(next) => {
				if (read !== reads.current) return
				if (next.outcome === 'refused' && next.reason === 'register_from_required' && !needsFrom) {
					setNeedsFrom(true)
					setFromDay(firstOfMonth(toDay || today))
					return
				}
				setAnswer(next)
			},
			(e) => {
				if (read === reads.current) setError(failure(e))
			},
		)
	}, [preview, period, partnerId, needsFrom, fromDay, toDay, today, rev])

	const shown = answer?.outcome === 'preview' ? answer.preview : null

	const build = async () => {
		if (!toDay) return
		setBuilding(true)
		setBuilt(null)
		try {
			const result = await calls.build({ ...period(), toDay })
			setBuilt(result)
			if (result.outcome === 'built') {
				onBuilt()
				// The lines are on it now: what follows is the next Register, up to today.
				setNeedsFrom(false)
				setFromDay('')
				setToDay(today)
				setRev((r) => r + 1)
			}
		} catch (e) {
			setError(failure(e))
		} finally {
			setBuilding(false)
		}
	}

	return (
		<section className='flex flex-col gap-4' data-testid='register-next'>
			<SectionTitle
				title={t(canWork ? 'partnerRegister.nextTitle' : 'partnerRegister.nextTitleSupport')}
				hint={t(canWork ? 'partnerRegister.explain' : 'partnerRegister.explainSupport')}
				aside={
					canWork &&
					shown && (
						<Button
							data-testid='register-build'
							disabled={building || !shown.periodEnded || shown.lines.length === 0}
							onClick={build}
						>
							{building ? t('partnerRegister.building') : t('partnerRegister.build')}
						</Button>
					)
				}
			/>
			<div className='flex flex-wrap items-center gap-x-6 gap-y-3'>
				<div className='flex items-center gap-2'>
					<Label htmlFor='register-partner'>{t('partnerRegister.partner')}</Label>
					<Select value={partnerId} onValueChange={setPartnerId}>
						<SelectTrigger
							id='register-partner'
							className='w-56'
							data-testid='register-partner'
							data-value={partnerId}
						>
							<SelectValue placeholder={t('partnerRegister.partnerPlaceholder')} />
						</SelectTrigger>
						<SelectContent>
							{(partners ?? []).map((p) => (
								<SelectItem key={p._id} value={p._id} data-testid={`register-partner-${p._id}`}>
									{p.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className='flex items-center gap-2'>
					<Label htmlFor='register-from'>{t('partnerRegister.from')}</Label>
					{needsFrom ? (
						<>
							<Input
								id='register-from'
								data-testid='register-from'
								type='date'
								className='w-40'
								max={toDay || today}
								value={fromDay}
								onChange={(e) => setFromDay(e.target.value)}
							/>
							<span className='flex' data-testid='register-first'>
								<Hint>{t('partnerRegister.firstHint')}</Hint>
							</span>
						</>
					) : (
						<>
							<span className='text-sm tabular-nums' data-testid='register-from-fixed'>
								{shown ? readableDay(shown.fromDay) : '—'}
							</span>
							{shown && <Hint>{t('partnerRegister.fromLast')}</Hint>}
						</>
					)}
				</div>
				<div className='flex items-center gap-2'>
					<Label htmlFor='register-to'>{t('partnerRegister.to')}</Label>
					<Input
						id='register-to'
						data-testid='register-to'
						type='date'
						className='w-40'
						max={today}
						value={toDay}
						onChange={(e) => setToDay(e.target.value)}
					/>
				</div>
			</div>

			{error && (
				<p role='alert' className='text-sm text-destructive' data-testid='register-error'>
					{error}
				</p>
			)}
			{built?.outcome === 'built' && (
				<p
					role='status'
					className='rounded-md border border-emerald-500/60 bg-emerald-500/10 px-4 py-2.5 font-semibold text-emerald-800 dark:text-emerald-300'
					data-testid='register-built'
				>
					{t('partnerRegister.built', {
						number: built.register.number,
						amount: formatMoney(built.register.totals.total, currencyWord(t, built.register.totals.currency)),
					})}
				</p>
			)}
			{built?.outcome === 'refused' && (
				<p role='alert' className='text-sm text-destructive' data-testid='register-build-refused'>
					{t(`partnerRegisterRefusal.${built.reason}` as TranslationKey)}
				</p>
			)}
			{answer?.outcome === 'refused' && (
				<p className='text-sm text-muted-foreground' data-testid='register-refused'>
					{t(`partnerRegisterRefusal.${answer.reason}` as TranslationKey)}
				</p>
			)}

			{shown && (
				<div className='flex flex-col gap-4' data-testid='register-preview'>
					{!shown.periodEnded && (
						<p className='text-sm text-amber-700 dark:text-amber-400' data-testid='register-unfinished'>
							{t(canWork ? 'partnerRegister.unfinished' : 'partnerRegister.unfinishedSupport')}
						</p>
					)}
					<TotalsLine totals={shown.totals} />
					<Lines lines={shown.lines} currency={shown.totals.currency} />
					<Waiting waiting={shown.waiting} currency={shown.totals.currency} />
				</div>
			)}
		</section>
	)
}

function currencyWord(t: ReturnType<typeof useTranslate>['t'], currency: string): string {
	return currency === 'UZS' ? t('partnerRegister.som') : currency
}

function TotalsLine({ totals }: { totals: Totals }) {
	const { t } = useTranslate()
	const currency = useCurrency()(totals.currency)
	return (
		<Stats testId='register-totals'>
			<Stat
				valueTestId='register-payments'
				label={t('partnerRegister.payments', { count: String(totals.collectedCount), currency })}
				value={amount(totals.collectedAmount)}
			/>
			<Stat
				valueTestId='register-cancellations'
				label={t('partnerRegister.cancellations', { count: String(totals.cancelledCount), currency })}
				value={amount(-totals.cancelledAmount)}
			/>
			<Stat valueTestId='register-total' label={t('partnerRegister.total', { currency })} value={amount(totals.total)} />
		</Stats>
	)
}

/** A Register's lines: each payment and, as a minus line, each cancellation. */
function Lines({ lines, currency }: { lines: Line[]; currency: string }) {
	const { t } = useTranslate()
	const word = useCurrency()(currency)
	const columns = useMemo<ColumnDef<Line>[]>(
		() => [
			{
				id: 'parcel',
				header: t('partnerRegister.colParcel'),
				cell: ({ row }) => (
					<Link to={`/parcels/${row.original.parcelId}`} className='font-mono hover:underline'>
						{row.original.trackingCode || row.original.parcelId}
					</Link>
				),
			},
			{ id: 'point', header: t('partnerRegister.colPoint'), cell: ({ row }) => row.original.point.locationId },
			{
				id: 'kind',
				header: t('partnerRegister.colKind'),
				cell: ({ row }) => (
					<div className='flex flex-wrap items-center gap-2'>
						<span className={cn(row.original.amount < 0 && 'text-muted-foreground')}>
							{t(`partnerRegister.kind_${row.original.kind}` as TranslationKey)}
						</span>
						{row.original.beforePeriod && (
							<Badge variant='outline' className='font-normal'>
								{t('partnerRegister.beforePeriod')}
							</Badge>
						)}
					</div>
				),
			},
			{
				id: 'bag',
				header: t('partnerRegister.colBag'),
				cell: ({ row }) => <span className='font-mono'>{row.original.cashBagCode ?? '—'}</span>,
			},
			{
				id: 'entered',
				header: t('partnerRegister.colEntered'),
				cell: ({ row }) => <span className='tabular-nums'>{formatDayTime(row.original.enteredAt)}</span>,
			},
			{
				id: 'amount',
				header: t('partnerRegister.colAmount', { currency: word }),
				meta: NUMBER,
				cell: ({ row }) => <span data-testid='register-line-amount'>{amount(row.original.amount)}</span>,
			},
		],
		[t, word],
	)
	const { table } = useDataTable({
		data: lines,
		columns,
		rowCount: lines.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (l) => l.eventId,
		initialState: { pagination: { pageIndex: 0, pageSize: PAGE_SIZE } },
	})
	return (
		<div data-testid='register-lines'>
			<DataTable
				table={table}
				withPagination={lines.length > PAGE_SIZE}
				emptyText={<span data-testid='register-no-lines'>{t('partnerRegister.noLines')}</span>}
				row={{ attributes: (l) => ({ 'data-testid': `register-line-${l.eventId}` }) }}
			/>
		</div>
	)
}

/** Support's cancellations whose cash is not at the central cashbox yet: they go in with it. */
function Waiting({ waiting, currency }: { waiting: Preview['waiting']; currency: string }) {
	const { t } = useTranslate()
	const word = useCurrency()(currency)
	if (waiting.length === 0) return null
	return (
		<section className='flex flex-col gap-2' data-testid='register-waiting'>
			<SectionTitle
				title={t('partnerRegister.waitingTitle')}
				count={waiting.length}
				hint={t('partnerRegister.waitingExplain')}
			/>
			<CompactTable>
				<TableHeader>
					<TableRow>
						<TableHead>{t('partnerRegister.colParcel')}</TableHead>
						<TableHead>{t('partnerRegister.colWhere')}</TableHead>
						<TableHead className='text-right'>{t('partnerRegister.colAmount', { currency: word })}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{waiting.map((w) => (
						<TableRow key={w.eventId}>
							<TableCell>
								<Link to={`/parcels/${w.parcelId}`} className='font-mono hover:underline'>
									{w.trackingCode || w.parcelId}
								</Link>
							</TableCell>
							<TableCell>
								{w.cashBagCode
									? t('partnerRegister.waitingInBag', { code: w.cashBagCode })
									: t('partnerRegister.waitingAtPoint')}
							</TableCell>
							<TableCell className='text-right tabular-nums'>{amount(w.amount)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</CompactTable>
		</section>
	)
}

// ---------------------------------------------------------------------------
// One Register, in the side panel
// ---------------------------------------------------------------------------

function OpenRegister({
	registerId,
	canWork,
	calls,
	onPaid,
}: {
	registerId: string
	canWork: boolean
	calls: RegisterCalls
	onPaid: () => void
}) {
	const { t } = useTranslate()
	const [register, setRegister] = useState<RegisterDetail | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [downloading, setDownloading] = useState(false)
	// The payment order's dialog opens inside the panel, so the panel keeps its place under it.
	const [paying, setPaying] = useState(false)
	// Read the Register again once it is marked paid.
	const [rev, setRev] = useState(0)
	const { detail } = calls

	useEffect(() => {
		detail(registerId).then(setRegister, (e) => setError(failure(e)))
	}, [detail, registerId, rev])

	const download = async () => {
		setDownloading(true)
		setError(null)
		try {
			calls.saveFile(await calls.exportFile(registerId))
		} catch (e) {
			setError(failure(e))
		} finally {
			setDownloading(false)
		}
	}

	return (
		<SheetContent
			className='w-full gap-0 data-[side=right]:sm:max-w-4xl'
			data-testid='register-detail'
		>
			<SheetHeader className='pr-12'>
				<SheetTitle className='flex flex-wrap items-center gap-2 text-lg font-semibold'>
					<span data-testid='register-number'>
						{register ? t('partnerRegister.detailTitle', { number: register.number }) : '…'}
					</span>
					{register && <StatusBadge register={register} />}
				</SheetTitle>
				<SheetDescription>
					{register &&
						t('partnerRegister.detailOf', {
							partner: register.partner.name,
							from: readableDay(register.fromDay),
							to: readableDay(register.toDay),
						})}
				</SheetDescription>
			</SheetHeader>
			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4'>
				{error && (
					<p role='alert' className='text-sm text-destructive' data-testid='register-open-error'>
						{error}
					</p>
				)}
				{register?.paid && (
					<p className='text-sm text-muted-foreground' data-testid='register-paid'>
						{t('partnerRegister.paidBy', {
							order: register.paid.paymentOrder,
							at: formatDateTime(register.paid.at),
							name: register.paid.by.name,
						})}
					</p>
				)}
				{register && (
					<>
						<TotalsLine totals={register.totals} />
						<Lines lines={register.lines} currency={register.totals.currency} />
					</>
				)}
			</div>
			{register && (
				<SheetFooter className='flex-row justify-end border-t'>
					<Button variant='outline' data-testid='register-download' disabled={downloading} onClick={download}>
						<Download />
						{downloading ? t('partnerRegister.downloading') : t('partnerRegister.download')}
					</Button>
					{canWork && register.status === 'to_pay' && (
						<Button data-testid='register-pay' onClick={() => setPaying(true)}>
							{t('partnerRegister.markPaid')}
						</Button>
					)}
				</SheetFooter>
			)}
			<Dialog open={paying} onOpenChange={setPaying}>
				{paying && register && (
					<MarkPaid
						register={register}
						calls={calls}
						onPaid={() => {
							setPaying(false)
							setRev((r) => r + 1)
							onPaid()
						}}
					/>
				)}
			</Dialog>
		</SheetContent>
	)
}

// ---------------------------------------------------------------------------
// «Отметить оплаченным» — the payment order's number, in a dialog
// ---------------------------------------------------------------------------

function MarkPaid({
	register,
	calls,
	onPaid,
}: {
	register: Pick<RegisterSummary, 'registerId' | 'number'>
	calls: RegisterCalls
	onPaid: () => void
}) {
	const { t } = useTranslate()
	const [order, setOrder] = useState('')
	const [marking, setMarking] = useState(false)
	const [refusal, setRefusal] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)

	const markPaid = async () => {
		if (marking || order.trim().length === 0) return
		setMarking(true)
		setRefusal(null)
		setError(null)
		try {
			const result = await calls.markPaid({ registerId: register.registerId, paymentOrder: order.trim() })
			if (result.outcome === 'refused') setRefusal(result.reason)
			else onPaid()
		} catch (e) {
			setError(failure(e))
		} finally {
			setMarking(false)
		}
	}

	return (
		<DialogContent className='sm:max-w-md' data-testid='register-pay-dialog'>
			<DialogHeader>
				<DialogTitle>{t('partnerRegister.markPaidTitle', { number: register.number })}</DialogTitle>
				<DialogDescription>{t('partnerRegister.markPaidHint')}</DialogDescription>
			</DialogHeader>
			<form
				className='flex flex-col gap-1.5'
				onSubmit={(e) => {
					e.preventDefault()
					void markPaid()
				}}
			>
				<Label htmlFor='register-order'>{t('partnerRegister.paymentOrder')}</Label>
				<Input
					id='register-order'
					data-testid='register-order'
					autoFocus
					value={order}
					maxLength={64}
					onChange={(e) => setOrder(e.target.value)}
				/>
			</form>
			{refusal && (
				<p role='alert' className='text-sm text-destructive' data-testid='register-mark-refused'>
					{t(`partnerRegisterRefusal.${refusal}` as TranslationKey)}
				</p>
			)}
			{error && (
				<p role='alert' className='text-sm text-destructive'>
					{error}
				</p>
			)}
			<DialogFooter>
				<DialogClose asChild>
					<Button variant='outline'>{t('partnerRegister.cancel')}</Button>
				</DialogClose>
				<Button data-testid='register-mark-paid' disabled={marking || order.trim().length === 0} onClick={markPaid}>
					{t('partnerRegister.markPaid')}
				</Button>
			</DialogFooter>
		</DialogContent>
	)
}

// ---------------------------------------------------------------------------
// Beside the central cashbox's money
// ---------------------------------------------------------------------------

export type RegisterTotalsRow = RouterOutputs['registers']['Totals'][number]

/**
 * Beside the central cashbox's money, per partner: what its Registers say is to be
 * paid and is paid, and what reached the cashbox but is on no Register yet (ticket
 * 15's question 6 — the cashbox's money only grows; what is paid out is here).
 */
/** One function for every render: the effect below reads again only when `load` changes. */
const loadRegisterTotals = () => trpcClient.registers.Totals.query()

export function RegisterTotalsBeside({
	load = loadRegisterTotals,
}: {
	load?: () => Promise<RegisterTotalsRow[]>
}) {
	const { t } = useTranslate()
	const [rows, setRows] = useState<RegisterTotalsRow[] | null>(null)
	useEffect(() => {
		load().then(setRows, () => setRows(null))
	}, [load])
	const currency = useCurrency()
	if (!rows || rows.length === 0) return null
	const word = currency(rows[0].currency)
	return (
		<div className='flex flex-col gap-2' data-testid='register-totals-beside'>
			<SectionTitle title={t('partnerRegister.besideTitle')} />
			<CompactTable>
				<TableHeader>
					<TableRow>
						<TableHead>{t('partnerRegister.colPartner')}</TableHead>
						<TableHead className='text-right'>{t('partnerRegister.besideToPay', { currency: word })}</TableHead>
						<TableHead className='text-right'>{t('partnerRegister.besidePaid', { currency: word })}</TableHead>
						<TableHead className='text-right'>{t('partnerRegister.besideOpen', { currency: word })}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((r) => (
						<TableRow key={r.partner.partnerId} data-testid={`register-totals-${r.partner.partnerId}`}>
							<TableCell>{r.partner.name}</TableCell>
							<TableCell className='text-right tabular-nums'>{amount(r.toPay)}</TableCell>
							<TableCell className='text-right tabular-nums'>{amount(r.paid)}</TableCell>
							<TableCell className='text-right tabular-nums'>{amount(r.notOnRegister)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</CompactTable>
		</div>
	)
}
